import { useEffect, useState, useCallback } from "react";
import { Inbox, Loader2, Mail, Phone, Globe, ExternalLink, Download, ChevronDown, ChevronUp, StickyNote, Send } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const SCOPE = {
  small: { emoji: "🟢", label: "Small", color: "#32D74B" },
  medium: { emoji: "🟡", label: "Medium", color: "#FFD60A" },
  large: { emoji: "🔴", label: "Large", color: "#FF3B30" },
};
const STATUSES = ["new", "contacted", "quoted", "won", "closed"];
const STATUS_COLOR = { new: "#4da3ff", contacted: "#FFD60A", quoted: "#9b5cff", won: "#32D74B", closed: "#8E8E93" };
const FUNNEL = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "quoted", label: "Quoted" },
  { key: "won", label: "Won" },
];

const Funnel = ({ counts = {}, total = 0 }) => {
  const won = counts.won || 0;
  const closed = counts.closed || 0;
  const winRate = total ? Math.round((won / total) * 100) : 0;
  const max = Math.max(1, ...FUNNEL.map((s) => counts[s.key] || 0));
  return (
    <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="lead-funnel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Pipeline funnel</h3>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-zinc-500">Total <b className="text-white">{total}</b></span>
          <span className="text-zinc-500">Win rate <b className="text-[#32D74B]" data-testid="funnel-winrate">{winRate}%</b></span>
          {closed > 0 && <span className="text-zinc-600">{closed} closed/lost</span>}
        </div>
      </div>
      <div className="space-y-2.5">
        {FUNNEL.map((s) => {
          const c = counts[s.key] || 0;
          const pct = total ? Math.round((c / total) * 100) : 0;
          return (
            <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-${s.key}`}>
              <span className="w-20 text-xs text-zinc-400 shrink-0">{s.label}</span>
              <div className="flex-1 h-7 bg-black/40 rounded-md overflow-hidden">
                <div className="h-full rounded-md flex items-center px-2 transition-all duration-500"
                  style={{ width: `${Math.max(c ? 8 : 0, (c / max) * 100)}%`, background: `${STATUS_COLOR[s.key]}33`, borderRight: c ? `2px solid ${STATUS_COLOR[s.key]}` : "none" }}>
                </div>
              </div>
              <span className="w-24 text-right text-xs shrink-0"><b className="text-white">{c}</b> <span className="text-zinc-600">· {pct}%</span></span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GOAL_LABEL = {
  migrate: "Move to Emergent", manage: "Managed care", upgrade: "Business upgrade", fix: "Fix on current host",
};
const TYPE_META = {
  migration: { label: "Migration", color: "#4da3ff" },
  service: { label: "Service", color: "#9b5cff" },
  outreach: { label: "Outreach", color: "#FF9F0A" },
};

export default function Leads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/leads");
      setData(res.data);
    } catch { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    setData((d) => ({ ...d, leads: d.leads.map((l) => l.lead_id === lead.lead_id ? { ...l, status } : l) }));
    try { await api.patch(`/admin/leads/${lead.lead_id}`, { status }); }
    catch { toast.error("Could not update status"); load(); }
  };

  const addNote = async (lead) => {
    const text = noteDraft.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const res = await api.post(`/admin/leads/${lead.lead_id}/note`, { text });
      setData((d) => ({ ...d, leads: d.leads.map((l) => l.lead_id === lead.lead_id
        ? { ...l, notes_log: [...(l.notes_log || []), res.data.note] } : l) }));
      setNoteDraft("");
      toast.success("Note added");
    } catch { toast.error("Could not add note"); }
    finally { setSavingNote(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/leads/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const leads = (data?.leads || []).filter((l) => filter === "all" || (l.status || "new") === filter);
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const toggle = (id) => { setExpanded((e) => (e === id ? null : id)); setNoteDraft(""); };

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#4da3ff] mb-2">
            <Inbox size={16} /><span className="text-[11px] font-bold uppercase tracking-[0.25em]">Admin</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-1">Leads &amp; quote requests</h1>
          <p className="text-zinc-400 text-sm mb-6">Move / Upgrade / Fix requests and logged outreach — expand a lead to add notes and work it to close.</p>
        </div>
        <button data-testid="export-csv-btn" onClick={exportCsv} disabled={exporting || !data?.total}
          className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center gap-2 disabled:opacity-50">
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
        </button>
      </div>

      {!loading && data?.total > 0 && <Funnel counts={data.counts} total={data.total} />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="lead-filters">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
            className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors capitalize ${filter === s ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}>
            {s}{s !== "all" && data?.counts?.[s] ? ` (${data.counts[s]})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 size={24} className="animate-spin" /></div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="leads-empty">
          <Inbox size={40} className="text-zinc-700 mx-auto" />
          <p className="text-zinc-400 mt-4">No leads {filter !== "all" ? `with status "${filter}"` : "yet"}.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {leads.map((l) => {
            const sc = SCOPE[l.scope];
            const tm = TYPE_META[l.type] || { label: l.type, color: "#8E8E93" };
            const isOpen = expanded === l.lead_id;
            const noteCount = (l.notes_log || []).length;
            return (
              <div key={l.lead_id} data-testid={`lead-${l.lead_id}`} className="bg-[#121212] border border-white/10 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold">{l.name || l.email || "(no name)"}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border" style={{ color: tm.color, borderColor: "rgba(255,255,255,0.12)" }}>
                        {l.type === "migration" ? GOAL_LABEL[l.goal] || "Migration" : (l.type === "outreach" ? "Outreach" : (l.service || "Service"))}
                      </span>
                      {sc && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ color: sc.color, background: `${sc.color}1a` }}>{sc.emoji} {sc.label}</span>}
                      {noteCount > 0 && <span className="text-[11px] text-zinc-500 flex items-center gap-1"><StickyNote size={11} /> {noteCount}</span>}
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-sm text-zinc-400 mt-2 font-mono">
                      {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail size={13} /> {l.email}</a>}
                      {l.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {l.phone}</span>}
                      {l.domain && <a href={`https://${l.domain}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-white"><Globe size={13} /> {l.domain} <ExternalLink size={11} /></a>}
                    </div>
                    {(l.notes || l.message) && <p className="text-sm text-zinc-300 mt-2 italic">"{l.notes || l.message}"</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xs text-zinc-600">{fmt(l.created_at)}</span>
                    <select
                      data-testid={`lead-status-${l.lead_id}`}
                      value={l.status || "new"}
                      onChange={(e) => setStatus(l, e.target.value)}
                      className="block mt-2 bg-black/40 border rounded-lg px-2.5 py-1.5 text-xs font-medium capitalize focus:outline-none"
                      style={{ color: STATUS_COLOR[l.status || "new"], borderColor: `${STATUS_COLOR[l.status || "new"]}55` }}
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#121212] text-white capitalize">{s}</option>)}
                    </select>
                  </div>
                </div>

                <button data-testid={`lead-toggle-${l.lead_id}`} onClick={() => toggle(l.lead_id)}
                  className="mt-3 text-xs text-[#4da3ff] hover:text-white inline-flex items-center gap-1 transition-colors">
                  {isOpen ? <>Hide details <ChevronUp size={13} /></> : <>Details &amp; notes <ChevronDown size={13} /></>}
                </button>

                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`lead-detail-${l.lead_id}`}>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">Details</h4>
                      <dl className="space-y-1.5 text-sm">
                        {[
                          ["Current host", l.currentHost], ["Registrar", l.registrar],
                          ["DNS", l.dnsProvider], ["Email provider", l.emailProvider],
                          ["Goal", GOAL_LABEL[l.goal] || l.goal], ["Service", l.service],
                          ["Sent by", l.sender], ["Est. savings", l.savingsMonthly ? `$${Math.round(l.savingsMonthly)}/mo` : ""],
                          ["Dependencies", (l.dependencies || []).join(", ")],
                        ].filter(([, v]) => v).map(([k, v]) => (
                          <div key={k} className="flex gap-3">
                            <dt className="text-zinc-500 w-28 shrink-0">{k}</dt>
                            <dd className="text-zinc-200 break-all">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">Notes</h4>
                      <div className="space-y-2 mb-3 max-h-40 overflow-y-auto" data-testid={`lead-notes-${l.lead_id}`}>
                        {noteCount === 0 ? (
                          <p className="text-zinc-600 text-sm">No notes yet.</p>
                        ) : (
                          (l.notes_log || []).map((n, i) => (
                            <div key={i} className="bg-black/30 border border-white/10 rounded-lg px-3 py-2">
                              <p className="text-sm text-zinc-200">{n.text}</p>
                              <p className="text-[10px] text-zinc-600 mt-1 font-mono">{n.author} · {fmt(n.ts)}</p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex items-start gap-2">
                        <textarea
                          data-testid={`note-input-${l.lead_id}`}
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          rows={2}
                          placeholder="Add a note…"
                          className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#007AFF]/50"
                        />
                        <button data-testid={`note-save-${l.lead_id}`} onClick={() => addNote(l)} disabled={savingNote || !noteDraft.trim()}
                          className="btn-brand text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50 flex items-center gap-1.5 shrink-0">
                          {savingNote ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
